import { useMemo, useState } from 'react';
import { CalendarClock, CalendarSearch, Check, Copy, ExternalLink, Info, Lock, MoveRight, Pencil, RotateCcw, Star, Trash2 } from 'lucide-react';
import {
  addDays,
  collectItems,
  createLinkedWork,
  createPrepWork,
  deadlineStatus,
  deleteEvent,
  deleteOccurrence,
  findPrepSlots,
  isDeadline,
  linkedPrep,
  resolveSourceReview,
  sanitizeUrl,
  setEventDone,
  setSourceDeadline,
  setSourceDone,
  setSourceNotes,
  sourceTreatsAsDeadlines,
  startDay,
  Temporal,
  type DisplayItem,
  type OccurrenceKey,
  type PrepOptions,
  type PrepResult,
  type PrepSlot,
  type SourceItem,
} from '@autoday/core';
import { getStore, useStore } from '../store/appStore.ts';
import { Badge, Button, Field, Sheet, inputCls } from '../ui/kit.tsx';
import { ItemIcon } from '../ui/icons.tsx';
import { catColor, timeLabel } from '../ui/format.ts';
import { errorText, toast } from '../ui/toast.tsx';
import { useToday } from '../ui/hooks.ts';
import { useUi } from '../App.tsx';
import { randomId } from '../platform.ts';
import { tr, useLang } from '../i18n/i18n.ts';
import { calendarName, categoryName, deadlineLabel, formatClock, formatDateLong, formatDayMonth, formatInstant, localizeError, tagExplanation, weekdayName } from '../i18n/format.ts';

/** Bara http(s)-länkar blir klickbara – oavsett var datan kom ifrån (import, synk, säkerhetskopia). */
const safeHref = (u: string | null | undefined): string | null => sanitizeUrl(u).url;
import { runMove } from '../actions/move.ts';
import { TagBadge } from './Agenda.tsx';

/** Nästa handling före lång bakgrundstext: "Gör så här:"-rad om den finns, annars första meningen. */
export function nextAction(description: string): { action: string | null; rest: string } {
  const lines = description.split('\n').map((l) => l.trim()).filter(Boolean);
  const idx = lines.findIndex((l) => /^(gör så här|åtgärd|att göra)\s*:/i.test(l));
  if (idx >= 0) return { action: lines[idx]!.replace(/^[^:]+:\s*/, ''), rest: lines.filter((_, i) => i !== idx).join('\n') };
  return { action: null, rest: description };
}

async function runOp(fn: Parameters<ReturnType<typeof getStore>['run']>[0], ok: string, undoable = true) {
  const store = getStore();
  try {
    const r = await store.run(fn);
    toast({ kind: 'ok', text: ok, action: undoable && r.undo ? { label: tr('Ångra', 'Undo'), run: () => void store.undo(r.undo!.opId).catch((e) => toast({ kind: 'error', text: errorText(e) })) } : undefined });
    return r;
  } catch (e) {
    toast({ kind: 'error', text: errorText(e) });
    return null;
  }
}

const hhmm = (local: string) => formatClock(Number(local.slice(11, 13)), Number(local.slice(14, 16)));

export function ItemDetail({ refId, occurrenceKey, intent, onClose }: { refId: string; occurrenceKey?: OccurrenceKey; intent?: 'prep'; onClose: () => void }) {
  const { state } = useStore();
  const today = useToday();
  const tz = state.settings.timeZone;
  const source = state.sourceItems[refId];
  const ev = state.events[refId];

  const item: DisplayItem | undefined = useMemo(() => {
    const key = occurrenceKey ?? (source ? startDay(source.data.timing, tz) : ev ? startDay(ev.timing, tz) : today);
    const list = collectItems(state, addDays(key, -1), addDays(key, 2));
    return list.find((i) => (i.ref.kind === 'local' ? i.ref.id : i.ref.key) === refId && (!occurrenceKey || i.occurrenceKey === occurrenceKey)) ?? list.find((i) => (i.ref.kind === 'local' ? i.ref.id : i.ref.key) === refId);
  }, [state, refId, occurrenceKey, source, ev, tz, today]);

  if (!item || (ev && ev.deleted)) {
    return (
      <Sheet open onOpenChange={(o) => !o && onClose()} title={tr('Posten finns inte', 'The item does not exist')} side>
        <p className="text-sm text-muted">{tr('Posten har tagits bort eller finns inte på denna enhet.', 'The item has been deleted or does not exist on this device.')}</p>
      </Sheet>
    );
  }
  return source ? <SourceDetail item={item} source={source} onClose={onClose} today={today} intent={intent} /> : <LocalDetail item={item} onClose={onClose} today={today} />;
}

function LocalDetail({ item, onClose, today }: { item: DisplayItem; onClose: () => void; today: string }) {
  const { state } = useStore();
  const ui = useUi();
  const { t } = useLang();
  const store = getStore();
  const tz = state.settings.timeZone;
  const ev = state.events[(item.ref as { id: string }).id]!;
  const [moveOpen, setMoveOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const linked = ev.linkedSourceKey ? state.sourceItems[ev.linkedSourceKey] : undefined;
  const now = () => store.clock.now();
  const linkedAfterDeadline = linked && compareDay(startDay(ev.timing, tz), startDay(linked.data.timing, tz)) > 0;

  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      side
      title={item.title}
      description={`${formatDateLong(startDay(item.timing, tz))} · ${timeLabel(item.timing, tz)}`}
      footer={
        <>
          <Button
            icon={<Pencil size={16} aria-hidden />}
            onClick={() => {
              onClose();
              ui.openEditor({ mode: 'edit', id: ev.id, occurrenceKey: ev.recurrence ? item.occurrenceKey : undefined });
            }}
          >
            {t('Redigera', 'Edit')}
          </Button>
          <Button
            variant={item.done ? 'secondary' : 'primary'}
            icon={item.done ? <RotateCcw size={16} aria-hidden /> : <Check size={16} aria-hidden />}
            onClick={() => void runOp((s) => setEventDone(s, randomId('dn_'), ev.id, !item.done, now(), ev.recurrence ? item.occurrenceKey : undefined), item.done ? t('Återöppnad.', 'Reopened.') : t('Markerad klar.', 'Marked done.'))}
          >
            {item.done ? t('Återöppna', 'Reopen') : t('Markera klar', 'Mark done')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-lg border border-border-strong" style={{ color: catColor(item.color) }}>
            <ItemIcon icon={item.icon} size={18} label />
          </span>
          <Badge>{categoryName(state.categories[item.categoryId], item.categoryId)}</Badge>
          <Badge>{calendarName(state.calendars[item.calendarId], item.calendarName)}</Badge>
          {item.recurring && <Badge tone="info">{t('Återkommande', 'Recurring')}</Badge>}
          {item.done && <Badge tone="ok">{t('Klar', 'Done')}</Badge>}
          {item.adjusted && <Badge tone="info">{t('Sommartidsjusterad', 'Adjusted for DST')}</Badge>}
          {item.priority === 'high' && !linked && <Badge tone="accent">{t('Visas under Viktigt', 'Shown under Deadlines')}</Badge>}
        </div>
        {linked && (
          <p className="rounded-lg border border-source/50 px-3 py-2">
            {t('Förberedelse inför', 'Preparation for')} <strong>{linked.data.summary}</strong> ({formatDateLong(startDay(linked.data.timing, tz), false)}). {t('Att flytta denna ändrar aldrig källdatumet.', 'Moving this never changes the source date.')}
            {linkedAfterDeadline && <span className="mt-1 block font-medium text-warn">{t('Den här förberedelsen ligger efter källans datum. Flytta den om den ska hinnas före.', 'This preparation is after the source date. Move it if it should happen before.')}</span>}
          </p>
        )}
        {item.location && (
          <p>
            <span className="text-muted">{t('Plats: ', 'Location: ')}</span>
            {item.location}
          </p>
        )}
        {item.notes && <p className="whitespace-pre-wrap">{item.notes}</p>}
        {safeHref(ev.url) && (
          <a href={safeHref(ev.url)!} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 text-accent hover:underline">
            <ExternalLink size={16} aria-hidden /> {t('Öppna länk', 'Open link')}
          </a>
        )}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{t('Påminnelser', 'Reminders')}</h3>
          {ev.alarms.length ? (
            <ul className="mt-1 list-disc pl-5">
              {ev.alarms.map((a) => (
                <li key={a.id}>{a.kind === 'relative' ? (a.offsetMinutes === 0 ? t('Vid start', 'At start') : `${fmtOffset(-a.offsetMinutes)} ${t('före', 'before')}`) : formatInstant(a.at, tz)}</li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">{t('Inga.', 'None.')}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button icon={<MoveRight size={16} aria-hidden />} onClick={() => setMoveOpen(true)}>
            {t('Flytta till datum', 'Move to date')}
          </Button>
          <Button
            icon={<Copy size={16} aria-hidden />}
            onClick={() => {
              onClose();
              ui.openCopy({ mode: 'day', date: startDay(item.timing, tz) });
            }}
          >
            {t('Kopiera dagen', 'Copy the day')}
          </Button>
          <Button variant="danger" icon={<Trash2 size={16} aria-hidden />} onClick={() => setConfirmDelete(true)}>
            {t('Ta bort', 'Delete')}
          </Button>
        </div>
        {confirmDelete && (
          <div className="rounded-lg border border-risk/60 bg-risk-bg p-3" role="alertdialog" aria-label={t('Bekräfta borttagning', 'Confirm deletion')}>
            <p>{ev.recurrence ? t('Ta bort denna förekomst eller hela serien? Du kan ångra direkt efteråt.', 'Delete this occurrence or the whole series? You can undo right afterwards.') : t(`Ta bort "${ev.title}"? Du kan ångra direkt efteråt.`, `Delete "${ev.title}"? You can undo right afterwards.`)}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button onClick={() => setConfirmDelete(false)}>{t('Avbryt', 'Cancel')}</Button>
              {ev.recurrence && (
                <Button
                  variant="danger"
                  onClick={async () => {
                    if (await runOp((s) => deleteOccurrence(s, randomId('del_'), ev.id, item.occurrenceKey, now()), t('Förekomsten borttagen.', 'Occurrence deleted.'))) onClose();
                  }}
                >
                  {t('Bara denna', 'Only this one')}
                </Button>
              )}
              <Button
                variant="danger"
                onClick={async () => {
                  if (await runOp((s) => deleteEvent(s, randomId('del_'), ev.id, now()), ev.recurrence ? t('Serien borttagen.', 'Series deleted.') : t('Borttagen.', 'Deleted.'))) onClose();
                }}
              >
                {ev.recurrence ? t('Hela serien', 'Whole series') : t('Ta bort', 'Delete')}
              </Button>
            </div>
          </div>
        )}
      </div>
      {moveOpen && <MoveToDate item={item} today={today} onClose={() => setMoveOpen(false)} />}
    </Sheet>
  );
}

function compareDay(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function fmtOffset(min: number): string {
  if (min % 10080 === 0) return tr(`${min / 10080} v`, `${min / 10080} wk`);
  if (min % 1440 === 0) return `${min / 1440} d`;
  if (min % 60 === 0) return `${min / 60} h`;
  return `${min} min`;
}

/** Tangentbordsvänligt alternativ till drag-and-drop – samma domänfunktion. */
function MoveToDate({ item, onClose }: { item: DisplayItem; today: string; onClose: () => void }) {
  const { state } = useStore();
  const { t } = useLang();
  const tz = state.settings.timeZone;
  const [date, setDate] = useState(startDay(item.timing, tz));
  const [time, setTime] = useState(item.timing.kind === 'timed' ? item.timing.start.slice(11, 16) : '');
  const [scope, setScope] = useState<'occurrence' | 'series'>('occurrence');
  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('Flytta till datum', 'Move to date')}
      description={item.title}
      footer={
        <>
          <Button onClick={onClose}>{t('Avbryt', 'Cancel')}</Button>
          <Button
            variant="primary"
            onClick={() => {
              void runMove(item, item.timing.kind === 'timed' && time ? { toStart: `${date}T${time}` } : { toDate: date }, item.recurring ? scope : 'series');
              onClose();
            }}
          >
            {t('Flytta', 'Move')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label={t('Nytt datum', 'New date')} htmlFor="mv-date">
          <input id="mv-date" type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        {item.timing.kind === 'timed' && (
          <Field label={t('Starttid', 'Start time')} htmlFor="mv-time" hint={t('Längden behålls.', 'The length is kept.')}>
            <input id="mv-time" type="time" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
        )}
        {item.recurring && (
          <fieldset className="flex flex-wrap gap-4 text-sm">
            <legend className="mb-1 font-medium">{t('Gäller', 'Applies to')}</legend>
            <label className="flex min-h-11 items-center gap-2">
              <input type="radio" checked={scope === 'occurrence'} onChange={() => setScope('occurrence')} className="h-5 w-5" /> {t('Bara denna', 'Only this one')}
            </label>
            <label className="flex min-h-11 items-center gap-2">
              <input type="radio" checked={scope === 'series'} onChange={() => setScope('series')} className="h-5 w-5" /> {t('Hela serien', 'Whole series')}
            </label>
          </fieldset>
        )}
      </div>
    </Sheet>
  );
}

function SourceDetail({ item, source, onClose, today, intent }: { item: DisplayItem; source: SourceItem; onClose: () => void; today: string; intent?: 'prep' }) {
  const { state } = useStore();
  const ui = useUi();
  const { t } = useLang();
  const store = getStore();
  const tz = state.settings.timeZone;
  const ov = state.overlays[source.key];
  const d = source.data;
  const date = startDay(d.timing, tz);
  const st = deadlineStatus(date, today, !!ov?.done);
  const { action, rest } = nextAction(d.description);
  const [confirm, setConfirm] = useState(false);
  const [notes, setNotes] = useState(ov?.notes ?? '');
  const [showAll, setShowAll] = useState(false);
  const defaultPrepTitle = t(`Förbered ${d.title.replace(/\s+\d{4}-\d{2}$/, '').toLowerCase()}`, `Prepare ${d.title.replace(/\s+\d{4}-\d{2}$/, '')}`);
  const [work, setWork] = useState<{ title: string; date: string; time: string } | null>(null);
  const recurringSource = !!d.recurrence || Object.values(state.sourceItems).some((c) => c.sourceId === source.sourceId && c.uid === source.uid && c.recurrenceId);
  const future = compareDay(date, today) > 0 || (d.timing.kind === 'timed' && Temporal.Instant.compare(Temporal.PlainDateTime.from(d.timing.start).toZonedDateTime(d.timing.timeZone).toInstant(), store.clock.now()) > 0);
  const [finding, setFinding] = useState(intent === 'prep' && !recurringSource && future);
  const src = state.sources[source.sourceId];
  const imp = src?.imports.filter((r) => !r.undone).find((r) => r.id === source.lastSeenImportId) ?? src?.imports.filter((r) => !r.undone).at(-1);
  const prep = linkedPrep(state, source.key);
  const srcLabel = !src || src.id === 'skattefix-ab' ? 'SkatteFix' : src.name;
  const now = () => store.clock.now();
  const nowI = store.clock.now();
  const counted = isDeadline(state, item);
  const sourceDefault = sourceTreatsAsDeadlines(src, source.sourceId);
  const choice = ov?.deadline ?? null;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} side title={d.title || d.summary} description={`${formatDateLong(date)} · ${srcLabel}`}>
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <TagBadge item={item} />
          <Badge tone={st.urgency === 'done' ? 'ok' : st.urgency === 'upcoming' ? 'neutral' : 'risk'}>{deadlineLabel(st)}</Badge>
          {d.transparent && <Badge title={t('Blockerar inte tid i krockvarningar', 'Does not block time in clash warnings')}>{t('Visas som ledig', 'Shown as free')}</Badge>}
          {counted && <Badge tone="accent">{t('Deadline', 'Deadline')}</Badge>}
        </div>

        {ov?.review && (
          <div className="rounded-lg border border-warn/60 bg-warn-bg p-3" role="status">
            <p className="font-semibold">
              {ov.review.reason === 'missingInLatest'
                ? t('Saknades i senaste importen', 'Missing in the latest import')
                : ov.review.reason === 'sourceChangedWhileDone'
                  ? t('Källan ändrades efter att du markerat klart här', 'The source changed after you marked it done here')
                  : t('Källan har ändrats', 'The source has changed')}
            </p>
            <p className="mt-1 text-muted">
              {ov.review.reason === 'missingInLatest'
                ? t('Posten kan vara hanterad, filtrerad eller utanför exportfönstret. Den har inte raderats eller klarmarkerats.', 'The item may be handled, filtered or outside the export window. It has not been deleted or marked done.')
                : ov.review.previousStart && ov.review.previousStart !== date
                  ? t(`Datum ändrat från ${formatDateLong(ov.review.previousStart, false)} till ${formatDateLong(date, false)}.`, `Date changed from ${formatDateLong(ov.review.previousStart, false)} to ${formatDateLong(date, false)}.`)
                  : t('Innehållet ändrades i senaste importen.', 'The content changed in the latest import.')}
              {ov.review.reason === 'sourceChangedWhileDone' && t(' Medan granskningen väntar gäller källans nya påminnelser.', ' While the review is pending, the source’s new reminders apply.')}
            </p>
            {prep.length > 0 && (
              <p className="mt-1">
                {t('Dina förberedelser har inte flyttats:', 'Your preparation has not been moved:')}{' '}
                {prep.map((p) => `${p.title} (${formatDayMonth(p.date)}${compareDay(p.date, date) >= 0 ? t(' – efter nya datumet', ' – after the new date') : ''})`).join(', ')}.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {ov.review.reason === 'sourceChangedWhileDone' && (
                <>
                  <Button onClick={() => void runOp((s) => resolveSourceReview(s, randomId('rv_'), source.key, now(), 'keepDone'), t('Behålls som klar här.', 'Kept as done here.'))}>{t('Behåll klar', 'Keep done')}</Button>
                  <Button onClick={() => void runOp((s) => resolveSourceReview(s, randomId('rv_'), source.key, now(), 'reopen'), t('Återöppnad.', 'Reopened.'))}>{t('Återöppna', 'Reopen')}</Button>
                </>
              )}
              {ov.review.reason !== 'sourceChangedWhileDone' && (
                <Button onClick={() => void runOp((s) => resolveSourceReview(s, randomId('rv_'), source.key, now(), 'acknowledge'), t('Granskad.', 'Reviewed.'))}>{t('Jag har sett ändringen', 'I have seen the change')}</Button>
              )}
            </div>
          </div>
        )}

        {action && (
          <div className="rounded-lg border border-accent/50 bg-raised p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-accent">{t('Nästa handling enligt källan', 'Next action according to the source')}</p>
            <p className="mt-1">{action}</p>
          </div>
        )}

        <p className="flex items-start gap-2 rounded-lg border border-source/50 px-3 py-2">
          <Lock size={16} className="mt-0.5 shrink-0 text-source" aria-hidden />
          <span>
            {t(`Källdatum från ${srcLabel}. Planera arbetet separat.`, `Source date from ${srcLabel}. Plan the work separately.`)}
            {d.tag.tag && <span className="mt-1 block text-xs text-muted">{tagExplanation(d.tag)}</span>}
          </span>
        </p>

        <div>
          <p className="whitespace-pre-wrap text-text">
            {showAll ? d.description : rest.slice(0, 400)}
            {!showAll && rest.length > 400 ? '…' : ''}
          </p>
          {rest.length > 400 && (
            <button className="mt-1 min-h-11 font-medium text-accent hover:underline" onClick={() => setShowAll((x) => !x)} aria-expanded={showAll}>
              {showAll ? t('Visa mindre', 'Show less') : t('Visa hela texten', 'Show the full text')}
            </button>
          )}
          <p className="mt-2 text-xs text-muted">
            {t('Originaltitel:', 'Original title:')} {d.summary}
          </p>
        </div>

        {safeHref(d.url) && (
          <a href={safeHref(d.url)!} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 font-medium text-accent hover:underline">
            <ExternalLink size={16} aria-hidden /> {t('Öppna originalet', 'Open the original')}
          </a>
        )}
        {d.rejectedUrl && (
          <p className="text-xs text-warn">
            {t('En osäker länk i källan visas inte som klickbar:', 'An unsafe link in the source is not shown as clickable:')} <code className="break-all">{d.rejectedUrl}</code>
          </p>
        )}

        <section aria-labelledby="sd-alarms">
          <h3 id="sd-alarms" className="text-xs font-semibold uppercase tracking-wide text-muted">
            {t(`Källans larm (${d.alarms.length})`, `Source alarms (${d.alarms.length})`)}
          </h3>
          <ul className="mt-1 space-y-0.5">
            {d.alarms.map((a) => {
              const past = a.kind === 'absolute' && Temporal.Instant.compare(Temporal.Instant.from(a.at), nowI) < 0;
              return (
                <li key={a.id} className={past ? 'text-muted line-through' : ''}>
                  {a.kind === 'absolute' ? formatInstant(a.at, tz) : t(`${-a.offsetMinutes} min före`, `${-a.offsetMinutes} min before`)}{' '}
                  {a.action !== 'DISPLAY' && (
                    <Badge tone="warn">
                      {a.action} – {t('används inte', 'not used')}
                    </Badge>
                  )}
                  {past && <span className="sr-only"> {t('(passerat, schemaläggs inte)', '(passed, not scheduled)')}</span>}
                </li>
              );
            })}
          </ul>
          {ov?.done && !ov.review && <p className="mt-1 text-xs text-muted">{t('Klar här – inga fler påminnelser för denna post.', 'Done here – no more reminders for this item.')}</p>}
        </section>

        <section aria-labelledby="sd-work" className="rounded-lg border border-border p-3">
          <h3 id="sd-work" className="flex items-center gap-2 font-semibold">
            <CalendarClock size={16} aria-hidden /> {t('Förbered', 'Prepare')}
          </h3>
          {prep.length > 0 ? (
            <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
              {prep.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className={`font-medium ${p.done ? 'text-muted line-through' : ''}`}>{p.title}</span>
                    <span className="block text-xs text-muted">
                      {weekdayName(Temporal.PlainDate.from(p.date).dayOfWeek, true)} {formatDayMonth(p.date)}
                      {p.timing.kind === 'timed' ? ` ${hhmm(p.timing.start)}–${hhmm(p.timing.end)}` : ''}
                      {compareDay(p.date, date) >= 0 && <span className="text-warn"> · {t('efter källans datum', 'after the source date')}</span>}
                    </span>
                  </span>
                  <Button className="min-h-10 px-3" onClick={() => ui.openItem({ ref: p.id })}>
                    {t('Öppna/flytta', 'Open/move')}
                  </Button>
                  <Button
                    variant="danger"
                    className="min-h-10 px-3"
                    aria-label={t(`Ta bort förberedelsen ${p.title}`, `Delete the preparation ${p.title}`)}
                    onClick={() => void runOp((s) => deleteEvent(s, randomId('del_'), p.id, now()), t('Förberedelsen borttagen. Källan är oförändrad.', 'Preparation deleted. The source is unchanged.'))}
                  >
                    <Trash2 size={16} aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-muted">{t('Ingen förberedelse planerad.', 'No preparation planned.')}</p>
          )}
          {recurringSource ? (
            <p className="mt-2 text-xs text-muted">{t('Återkommande källdatum: AutoDay letar inte luckor åt en viss förekomst ännu. Välj tid själv nedan.', 'Recurring source date: AutoDay does not look for gaps for a specific occurrence yet. Choose a time yourself below.')}</p>
          ) : !future ? (
            <p className="mt-2 text-xs text-muted">{t('Datumet har passerat – inga luckor att föreslå.', 'The date has passed – no gaps to suggest.')}</p>
          ) : null}
          {finding ? (
            <PrepFinder source={source} defaultTitle={defaultPrepTitle} onDone={() => setFinding(false)} onManual={() => { setFinding(false); setWork({ title: defaultPrepTitle, date: addDays(date, -3) < today ? today : addDays(date, -3), time: '09:00' }); }} />
          ) : !work ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {!recurringSource && future && (
                <Button variant="primary" icon={<CalendarSearch size={16} aria-hidden />} onClick={() => setFinding(true)}>
                  {t('Hitta tid att förbereda', 'Find time to prepare')}
                </Button>
              )}
              <Button onClick={() => setWork({ title: defaultPrepTitle, date: addDays(date, -3) < today ? today : addDays(date, -3), time: '09:00' })}>{t('Välj tid själv', 'Choose a time yourself')}</Button>
            </div>
          ) : (
            <div className="mt-2 grid gap-3">
              <Field label={t('Titel', 'Title')} htmlFor="lw-title">
                <input id="lw-title" className={inputCls} value={work.title} onChange={(e) => setWork({ ...work, title: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('Datum', 'Date')} htmlFor="lw-date">
                  <input id="lw-date" type="date" className={inputCls} value={work.date} max={date} onChange={(e) => setWork({ ...work, date: e.target.value })} />
                </Field>
                <Field label={t('Tid', 'Time')} htmlFor="lw-time">
                  <input id="lw-time" type="time" className={inputCls} value={work.time} onChange={(e) => setWork({ ...work, time: e.target.value })} />
                </Field>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setWork(null)}>{t('Avbryt', 'Cancel')}</Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    if (
                      await runOp(
                        (s) => createLinkedWork(s, randomId('lw_'), source.key, { title: work.title, date: work.date, startTime: work.time || undefined, durationMinutes: 60, notes: t(`Förberedelse inför: ${d.summary}`, `Preparation for: ${d.summary}`) }, now()),
                        t('Arbetsaktivitet skapad. Den kan flyttas fritt.', 'Work activity created. It can be moved freely.'),
                      )
                    )
                      setWork(null);
                  }}
                >
                  {t('Skapa', 'Create')}
                </Button>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border p-3" aria-labelledby="sd-deadline">
          <h3 id="sd-deadline" className="flex items-center gap-2 font-semibold">
            <Star size={16} aria-hidden /> {t('Viktigt', 'Deadlines')}
          </h3>
          <p className="mt-1 text-muted">
            {counted ? t('Räknas som deadline och visas under Viktigt.', 'Counts as a deadline and is shown under Deadlines.') : t('Räknas inte som deadline – visas bara i kalendern.', 'Does not count as a deadline – only shown in the calendar.')}{' '}
            {choice === null
              ? sourceDefault
                ? t(`Standard för ${srcLabel}.`, `Default for ${srcLabel}.`)
                : t(`Standard för ${srcLabel}: importerade poster är vanliga kalenderbesök.`, `Default for ${srcLabel}: imported items are ordinary calendar entries.`)
              : t('Ditt val för den här posten.', 'Your choice for this item.')}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {!counted && (
              <Button onClick={() => void runOp((s) => setSourceDeadline(s, randomId('dl_'), source.key, true, now()), t('Visas under Viktigt.', 'Shown under Deadlines.'))}>{t('Räkna som deadline', 'Count as a deadline')}</Button>
            )}
            {counted && (
              <Button onClick={() => void runOp((s) => setSourceDeadline(s, randomId('dl_'), source.key, false, now()), t('Visas inte längre under Viktigt.', 'No longer shown under Deadlines.'))}>{t('Inte en deadline', 'Not a deadline')}</Button>
            )}
            {choice !== null && (
              <Button variant="ghost" onClick={() => void runOp((s) => setSourceDeadline(s, randomId('dl_'), source.key, null, now()), t('Källans standard gäller.', 'The source default applies.'))}>
                {t('Använd källans standard', 'Use the source default')}
              </Button>
            )}
          </div>
        </section>

        <Field label={t('Egna anteckningar (sparas separat från källan)', 'Your own notes (saved separately from the source)')} htmlFor="sd-notes">
          <textarea
            id="sd-notes"
            className={`${inputCls} min-h-20 py-2`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (ov?.notes ?? '') && void runOp((s) => setSourceNotes(s, randomId('nt_'), source.key, notes, now()), t('Anteckning sparad.', 'Note saved.'), false)}
          />
        </Field>

        <section className="rounded-lg border border-border p-3">
          {ov?.done ? (
            <>
              <p className="flex items-center gap-2">
                <Check size={16} className="text-ok" aria-hidden /> {t('Markerad klar här', 'Marked done here')} {formatInstant(ov.done.at, tz)}.
              </p>
              <p className="mt-1 text-xs text-muted">{t(`Detta är en lokal kvittering i AutoDay – inte ett inlämningskvitto och inget meddelande till ${srcLabel}.`, `This is a local check-off in AutoDay – not a submission receipt and no message to ${srcLabel}.`)}</p>
              <Button className="mt-2" icon={<RotateCcw size={16} aria-hidden />} onClick={() => void runOp((s) => setSourceDone(s, randomId('dn_'), source.key, false, now()), t('Återöppnad.', 'Reopened.'))}>
                {t('Återöppna', 'Reopen')}
              </Button>
            </>
          ) : (
            <>
              <label className="flex min-h-11 items-start gap-2">
                <input type="checkbox" className="mt-1 h-5 w-5" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
                <span>{t('Jag har kontrollerat att åtgärden är utförd (t.ex. mot kvittens eller originalet).', 'I have checked that the action is done (e.g. against a receipt or the original).')}</span>
              </label>
              <Button className="mt-2" variant="primary" disabled={!confirm} icon={<Check size={16} aria-hidden />} onClick={() => void runOp((s) => setSourceDone(s, randomId('dn_'), source.key, true, now(), true), t('Markerad klar här.', 'Marked done here.'))}>
                {t('Markera klar här', 'Mark done here')}
              </Button>
              <p className="mt-1 text-xs text-muted">
                {t(`Lokal kvittering – rapporteras inte till ${srcLabel} eller någon myndighet. Länkar och passerade datum markerar aldrig något som klart.`, `Local check-off – not reported to ${srcLabel} or any authority. Links and passed dates never mark anything as done.`)}
              </p>
            </>
          )}
        </section>

        <section className="flex items-start gap-2 text-xs text-muted">
          <Info size={14} className="mt-0.5 shrink-0" aria-hidden />
          <p>
            {t('Importerad ögonblicksbild, inte live-synkning.', 'Imported snapshot, not a live sync.')}
            {imp
              ? t(
                  ` Importerad ${formatInstant(imp.at, tz)} från ${imp.fileName}. Filens DTSTAMP: ${imp.fileDtstamp ? formatInstant(imp.fileDtstamp, tz) : 'saknas'}. Senaste datum i filen: ${imp.lastDate ?? '–'}.`,
                  ` Imported ${formatInstant(imp.at, tz)} from ${imp.fileName}. File DTSTAMP: ${imp.fileDtstamp ? formatInstant(imp.fileDtstamp, tz) : 'missing'}. Latest date in the file: ${imp.lastDate ?? '–'}.`,
                )
              : ''}{' '}
            {t(`Ändringar eller "Hanterad" i ${srcLabel} uppdaterar inte denna kopia. Importen verifierar inte myndighetsregler eller att en deklaration lämnats.`, `Changes or "Handled" in ${srcLabel} do not update this copy. The import does not verify authority rules or that a return has been filed.`)}
          </p>
        </section>
      </div>
    </Sheet>
  );
}

const WINDOWS: Array<{ id: string; sv: string; en: string; start: string; end: string }> = [
  { id: 'workday', sv: 'Arbetsdag 08–17', en: 'Work day 08–17', start: '08:00', end: '17:00' },
  { id: 'morning', sv: 'Förmiddag 08–12', en: 'Morning 08–12', start: '08:00', end: '12:00' },
  { id: 'afternoon', sv: 'Eftermiddag 13–17', en: 'Afternoon 13–17', start: '13:00', end: '17:00' },
  { id: 'evening', sv: 'Kväll 17–21', en: 'Evening 17–21', start: '17:00', end: '21:00' },
];

/**
 * "Hitta tid att förbereda": användaren väljer längd och sökperiod, AutoDay föreslår upp till tre luckor
 * före datumet (lokal, deterministisk sökning i core/prep.ts). Inget skapas förrän användaren valt en
 * lucka; luckan kontrolleras igen vid skapandet och ett operations-ID per sökning gör dubbelklick ofarligt.
 */
function PrepFinder({ source, defaultTitle, onDone, onManual }: { source: SourceItem; defaultTitle: string; onDone: () => void; onManual: () => void }) {
  const { t } = useLang();
  const store = getStore();
  const [duration, setDuration] = useState(60);
  const [windowId, setWindowId] = useState('workday');
  const [days, setDays] = useState<'weekdays' | 'all'>('weekdays');
  const [lookahead, setLookahead] = useState(14);
  const [title, setTitle] = useState(defaultTitle);
  const [result, setResult] = useState<PrepResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [opId, setOpId] = useState(() => randomId('prep_'));
  const win = WINDOWS.find((w) => w.id === windowId)!;
  const opts: PrepOptions = { durationMinutes: duration, windowStart: win.start, windowEnd: win.end, days, lookaheadDays: lookahead };

  const search = () => {
    setResult(findPrepSlots(store.state, source.key, opts, store.clock.now()));
    setOpId(randomId('prep_'));
  };

  async function create(slot: PrepSlot) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await store.run((s) => createPrepWork(s, opId, source.key, slot, { title: title.trim() || defaultTitle, notes: t(`Förberedelse inför: ${source.data.summary}`, `Preparation for: ${source.data.summary}`) }, store.clock.now()));
      toast({
        kind: 'ok',
        text: r.duplicate ? t('Förberedelsen fanns redan – ingen dubblett skapades.', 'The preparation already existed – no duplicate was created.') : t('Förberedelse skapad. Källans datum är oförändrat.', 'Preparation created. The source date is unchanged.'),
        action: r.undo ? { label: t('Ångra', 'Undo'), run: () => void store.undo(r.undo!.opId).catch((e) => toast({ kind: 'error', text: errorText(e) })) } : undefined,
      });
      onDone();
    } catch (e) {
      // Luckan hann bli upptagen: sök om och visa resultatet.
      toast({ kind: 'warn', text: localizeError(errorText(e)) });
      setResult(findPrepSlots(store.state, source.key, opts, store.clock.now()));
      setOpId(randomId('prep_'));
    } finally {
      setBusy(false);
    }
  }

  const explain = (s: PrepSlot) => {
    const parts: string[] = [];
    parts.push(s.daysBefore === 1 ? t('dagen före', 'the day before') : t(`${s.daysBefore} dagar före`, `${s.daysBefore} days before`));
    if (s.before) parts.push(t(`efter ${s.before.title} (slutar ${hhmm('0000-00-00T' + s.before.end!)})`, `after ${s.before.title} (ends ${hhmm('0000-00-00T' + s.before.end!)})`));
    if (s.after) parts.push(t(`före ${s.after.title} (${hhmm('0000-00-00T' + s.after.start!)})`, `before ${s.after.title} (${hhmm('0000-00-00T' + s.after.start!)})`));
    if (!s.before && !s.after) parts.push(s.busySameDay ? t('ledigt i sökperioden', 'free in the search period') : t('inget annat i sökperioden den dagen', 'nothing else in the search period that day'));
    return parts.join(' · ');
  };

  return (
    <div className="mt-2 grid gap-3 rounded-lg border border-accent-line bg-accent-soft/40 p-3" role="group" aria-label={t('Hitta tid att förbereda', 'Find time to prepare')}>
      <Field label={t('Titel på förberedelsen', 'Title of the preparation')} htmlFor="pf-title">
        <input id="pf-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
      </Field>
      <fieldset>
        <legend className="mb-1.5 text-sm font-medium">{t('Hur lång tid?', 'How long?')}</legend>
        <div className="flex flex-wrap gap-1.5">
          {[30, 45, 60].map((m) => (
            <label key={m} className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-full border px-3 ${duration === m ? 'border-accent bg-raised font-semibold' : 'border-border-strong text-muted'}`}>
              <input type="radio" name="pf-dur" className="sr-only" checked={duration === m} onChange={() => setDuration(m)} />
              {m} min
            </label>
          ))}
        </div>
      </fieldset>
      {/* Kolumner efter panelens egen bredd (sidopanelen är smal även på dator). */}
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(9.5rem,1fr))]">
        <Field label={t('När på dagen', 'Time of day')} htmlFor="pf-win">
          <select id="pf-win" className={inputCls} value={windowId} onChange={(e) => setWindowId(e.target.value)}>
            {WINDOWS.map((w) => (
              <option key={w.id} value={w.id}>
                {t(w.sv, w.en)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('Dagar', 'Days')} htmlFor="pf-days">
          <select id="pf-days" className={inputCls} value={days} onChange={(e) => setDays(e.target.value as 'weekdays' | 'all')}>
            <option value="weekdays">{t('Vardagar', 'Weekdays')}</option>
            <option value="all">{t('Alla dagar', 'All days')}</option>
          </select>
        </Field>
        <Field label={t('Sök bakåt från datumet', 'Search back from the date')} htmlFor="pf-look">
          <select id="pf-look" className={inputCls} value={lookahead} onChange={(e) => setLookahead(Number(e.target.value))}>
            {[7, 14, 21].map((n) => (
              <option key={n} value={n}>
                {t(`${n} dagar`, `${n} days`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" icon={<CalendarSearch size={16} aria-hidden />} onClick={search}>
          {result ? t('Sök igen', 'Search again') : t('Föreslå tider', 'Suggest times')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('Avbryt', 'Cancel')}
        </Button>
      </div>

      {result && result.status !== 'unsupported' && (
        <div aria-live="polite" className="grid gap-2">
          {result.slots.length ? (
            <>
              <p className="font-medium">{t(`${result.slots.length} möjliga tider före ${formatDayMonth(result.deadlineDate)}:`, `${result.slots.length} possible times before ${formatDayMonth(result.deadlineDate)}:`)}</p>
              <ul className="grid gap-2">
                {result.slots.map((s) => (
                  <li key={s.start} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-panel px-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="font-semibold">
                        {weekdayName(Temporal.PlainDate.from(s.date).dayOfWeek)} {formatDayMonth(s.date)} {hhmm(s.start)}–{hhmm(s.end)}
                      </span>
                      <span className="block text-xs text-muted">{explain(s)}</span>
                    </span>
                    <Button variant="primary" className="min-h-10 px-3" disabled={busy} onClick={() => void create(s)}>
                      {t('Skapa här', 'Create here')}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="rounded-lg border border-border bg-panel p-3">
              <p className="font-medium">{t('Inga lediga tider med de här valen.', 'No free times with these choices.')}</p>
              <p className="mt-1 text-muted">{t('Prova en annan del av dagen, fler dagar eller kortare tid – eller välj tid själv.', 'Try another part of the day, more days or a shorter time – or choose a time yourself.')}</p>
              <Button className="mt-2" onClick={onManual}>
                {t('Välj tid själv', 'Choose a time yourself')}
              </Button>
            </div>
          )}
          <p className="text-xs text-muted">
            {t(
              `Sökt ${formatDayMonth(result.searchedFrom)}–${formatDayMonth(result.searchedTo)} i kalenderdata som finns i AutoDay på den här enheten: egna aktiviteter (även dolda kalendrar och heldagar) och importerade poster som inte är markerade som lediga. En importerad fil är en ögonblicksbild och kan vara äldre än originalkalendern.`,
              `Searched ${formatDayMonth(result.searchedFrom)}–${formatDayMonth(result.searchedTo)} in calendar data in AutoDay on this device: your own activities (including hidden calendars and all-day items) and imported items not marked as free. An imported file is a snapshot and may be older than the original calendar.`,
            )}
            {result.uncertain.length > 0 &&
              t(
                ` ${result.uncertain.length} återkommande post${result.uncertain.length === 1 ? '' : 'er'} kunde inte tolkas helt och räknas som upptagen varje dag.`,
                ` ${result.uncertain.length} recurring item${result.uncertain.length === 1 ? '' : 's'} could not be fully read and count${result.uncertain.length === 1 ? 's' : ''} as busy every day.`,
              )}
          </p>
        </div>
      )}
      {result?.status === 'unsupported' && (
        <p className="rounded-lg border border-border bg-panel p-3">
          {result.reason === 'recurring-source'
            ? t('Återkommande källdatum stöds inte här ännu. Välj tid själv.', 'Recurring source dates are not supported here yet. Choose a time yourself.')
            : result.reason === 'past'
              ? t('Datumet har passerat.', 'The date has passed.')
              : result.reason === 'invalid-options'
                ? t('Sökperioden är kortare än förberedelsens längd.', 'The search period is shorter than the length of the preparation.')
                : t('Posten finns inte längre.', 'The item no longer exists.')}
        </p>
      )}
    </div>
  );
}
